import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase, isSupabaseConfigured } from "./supabaseClient";

// Push notification plumbing (native app only — every export is a safe no-op
// on the web build). Device tokens land in the `push_tokens` table; the
// content pipeline reads that table to send "new analyses" pushes via APNs.
//
// Permission is requested ONLY from the bell button in the header
// (enablePush), never unprompted at launch: initPushNotifications just
// re-registers devices that already granted permission earlier.
//
// The plugin is imported statically: a build shipped with a lazy
// import() here and the bell reported 'unsupported' on a device whose
// binary demonstrably linked PushNotificationsPlugin.o.

let listenersAttached = false;

// Populated when a plugin call fails on a native platform, so the UI can
// surface WHY instead of a silent "unsupported".
export let lastPushError = "";

function attachListeners() {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener("registration", async (token) => {
    if (isSupabaseConfigured() && token && token.value) {
      // UNIQUE constraint makes re-registration a harmless 409.
      await supabase
        .from("push_tokens")
        .insert({ token: token.value, platform: Capacitor.getPlatform() });
    }
  });

  // Tapping a push opens the analysis it announces.
  PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
    const data = (action.notification && action.notification.data) || {};
    if (data.analysisId) {
      window.location.href = "/analysis/" + String(data.analysisId);
    }
  });
}

// 'unsupported' (web) | 'prompt' | 'granted' | 'denied'
export async function getPushStatus() {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === "granted") return "granted";
    if (perm.receive === "denied") return "denied";
    return "prompt";
  } catch (e) {
    lastPushError = String((e && e.message) || e);
    return "unsupported";
  }
}

// Bell tap: ask for permission (iOS shows its dialog only once ever) and
// register this device. Returns the resulting status string.
export async function enablePush() {
  if (!Capacitor.isNativePlatform()) return "unsupported";
  try {
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return "denied";
    attachListeners();
    await PushNotifications.register();
    return "granted";
  } catch (e) {
    lastPushError = String((e && e.message) || e);
    return "unsupported";
  }
}

// Launch path: silently refresh the registration of already-opted-in devices.
export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive !== "granted") return;
    attachListeners();
    await PushNotifications.register();
  } catch (_) {
    // Push is best-effort; never let it break app startup.
  }
}
